import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
    Alert,
    Button,
    Center,
    Container,
    Loader,
    SegmentedControl,
    Text,
} from '@mantine/core';
import { useQuery } from '@apollo/client';
import { IconFolders, IconUpload } from '@tabler/icons-react';

import { DashboardHeader, When } from '../../../components/common';
import {
    TrainingCategoryManager,
    TrainingUploadForm,
} from '../../../components/training';
import { GET_TRAINING_CATEGORIES } from '../../../apollo/queries/training';
import { getErrorMessage } from '../../../helpers/getErrorMessage';
import { useUser } from '../../../hooks/useUser';
import { TrainingCategory } from '../../../types/training';

type UploadTab = 'categories' | 'upload';

function TrainingUploadPage() {
    const { isSuperAdmin } = useUser();
    const { data, loading, error, refetch } = useQuery(GET_TRAINING_CATEGORIES);
    const [activeTab, setActiveTab] = useState<UploadTab>('categories');

    const categories: TrainingCategory[] = data?.trainingCategories ?? [];
    const hasCategories = categories.length > 0;

    useEffect(() => {
        if (!loading && !error && !hasCategories) {
            setActiveTab('categories');
        }
    }, [loading, error, hasCategories]);

    const handleTabChange = (value: string) => {
        setActiveTab(value as UploadTab);
    };

    const handleGoToCategories = () => {
        setActiveTab('categories');
    };

    const handleGoToUpload = () => {
        setActiveTab('upload');
    };

    const handleUploaded = () => {
        refetch().catch(() => undefined);
    };

    if (!isSuperAdmin) {
        return <Navigate to="/training/materials" replace />;
    }

    return (
        <div className="relative pb-10">
            <DashboardHeader
                title="Training - Material upload"
                hasBreadcrumb={false}
                hasDate
            />
            <div className="px-11 w-full mb-8">
                <When
                    condition={!loading}
                    fallback={
                        <Center className="py-16">
                            <Loader />
                        </Center>
                    }
                >
                    <When
                        condition={!error}
                        fallback={
                            <Container
                                size="xl"
                                className="rounded-lg p-4 bg-white"
                            >
                                <Alert
                                    color="red"
                                    title="Unable to load categories"
                                >
                                    {getErrorMessage(error)}
                                </Alert>
                            </Container>
                        }
                    >
                        <Container
                            size="xl"
                            className="rounded-lg p-4 bg-white"
                        >
                            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2">
                                    <When
                                        condition={activeTab === 'categories'}
                                        fallback={<IconUpload size={24} />}
                                    >
                                        <IconFolders size={24} />
                                    </When>
                                    <span className="text-lg font-medium whitespace-nowrap">
                                        <When
                                            condition={
                                                activeTab === 'categories'
                                            }
                                            fallback="Upload material"
                                        >
                                            Manage categories
                                        </When>
                                    </span>
                                </div>
                                <SegmentedControl
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    data={[
                                        {
                                            label: '1. Categories',
                                            value: 'categories',
                                        },
                                        {
                                            label: '2. Upload material',
                                            value: 'upload',
                                        },
                                    ]}
                                />
                            </div>

                            <When
                                condition={activeTab === 'categories'}
                                fallback={
                                    <When
                                        condition={hasCategories}
                                        fallback={
                                            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                                                <Text
                                                    size="lg"
                                                    fw={500}
                                                    className="mb-2"
                                                >
                                                    Create a category first
                                                </Text>
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                    className="mb-4"
                                                >
                                                    Categories control audience
                                                    visibility. Add at least one
                                                    before uploading materials.
                                                </Text>
                                                <Button
                                                    size="sm"
                                                    className="w-fit"
                                                    onClick={
                                                        handleGoToCategories
                                                    }
                                                >
                                                    Go to categories
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <TrainingUploadForm
                                            categories={categories}
                                            onUploaded={handleUploaded}
                                        />
                                    </When>
                                }
                            >
                                <>
                                    <TrainingCategoryManager
                                        categories={categories}
                                    />
                                    <When condition={hasCategories}>
                                        <div className="mt-6 flex justify-end">
                                            <Button
                                                size="sm"
                                                className="w-fit"
                                                onClick={handleGoToUpload}
                                            >
                                                Continue to upload
                                            </Button>
                                        </div>
                                    </When>
                                </>
                            </When>
                        </Container>
                    </When>
                </When>
            </div>
        </div>
    );
}

export default TrainingUploadPage;
